import * as React from "react"
import { cn } from "@/lib/utils"

export interface PaginationProps {
  page: number
  total: number // total de páginas
  onChange: (p:number)=>void
  className?: string
}

function usePageNumbers(page: number, total: number, maxButtons = 7) {
  return React.useMemo<(number|"...")[]>(() => {
    if (total <= 1) return [1]
    const pages: (number|"...")[] = []
    const clamped = Math.max(1, Math.min(page, total))
    const side = Math.max(1, Math.floor((maxButtons - 3) / 2)) // lados do current (sem first/last)
    const start = Math.max(2, clamped - side)
    const end = Math.min(total - 1, clamped + side)

    pages.push(1)
    if (start > 2) pages.push("...")
    for (let p = start; p <= end; p++) pages.push(p)
    if (end < total - 1) pages.push("...")
    if (total > 1) pages.push(total)
    return Array.from(new Set(pages.filter(Boolean))) as (number|"...")[]
  }, [page, total, maxButtons])
}

export function Pagination({ page, total, onChange, className }: PaginationProps) {
  const prev = () => onChange(Math.max(1, page - 1))
  const next = () => onChange(Math.min(total, page + 1))
  const nums = usePageNumbers(page, total)
  const [goto, setGoto] = React.useState<string>(String(page))
  React.useEffect(() => setGoto(String(page)), [page])

  const onGotoKey: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter') {
      const n = Number(goto)
      if (!Number.isNaN(n) && n >= 1 && n <= total) onChange(n)
    }
  }

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      <button aria-label="Página anterior" className="px-2 py-1 rounded border border-border bg-background disabled:opacity-50" disabled={page<=1} onClick={prev}>Retornar</button>
      {/* Números ocultos no mobile */}
      <div className="hidden md:flex items-center gap-1">
        {nums.map((n, idx) => n === '...'
          ? <span key={`e-${idx}`} className="px-2 text-foreground/60">…</span>
          : (
            <button
              key={n}
              aria-label={`Ir para página ${n}`}
              className={cn(
                "min-w-8 px-2 py-1 rounded border text-sm",
                n === page ? "border-primary bg-primary/10" : "border-border bg-background hover:bg-muted"
              )}
              onClick={() => onChange(n)}
            >{n}</button>
          )
        )}
      </div>
      <button aria-label="Próxima página" className="px-2 py-1 rounded border border-border bg-background disabled:opacity-50" disabled={page>=total} onClick={next}>Avançar</button>
      {/* Total oculto no mobile */}
      <span className="ml-1 hidden text-sm text-foreground/60 md:inline">de {total}</span>
      {/* Ir para: sempre visível */}
      <div className="ml-2 flex items-center gap-1 text-sm">
        <span>Ir para</span>
        <input
          aria-label="Ir para página"
          type="number"
          min={1}
          max={Math.max(1, total)}
          value={goto}
          onChange={(e)=> setGoto(e.target.value)}
          onKeyDown={onGotoKey}
          className="h-8 w-16 rounded border border-border bg-background px-2 text-sm"
        />
      </div>
    </div>
  )
}

